/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Partner Detail Modal - Neo-Cyberpunk Partner Profile
 * ============================================================================
 *
 * A futuristic, high-tech partner profile display with holographic effects,
 * animated scan lines, and print-optimized layout.
 */

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, CheckCircle, XCircle, Clock, Zap, Briefcase, Mail, Building2, TrendingUp, Percent, Users } from 'lucide-react';
import { PartnerListItem, PartnerStatus, PartnerType } from '@/types/superadmin.types';

interface PartnerDetailModalProps {
  partner: PartnerListItem | null;
  isOpen: boolean;
  onClose: () => void;
}

// Futuristic timestamp formatter
const formatFuturisticDate = (dateStr: string | undefined) => {
  if (!dateStr) return '----.--.--.--:--:--';
  const date = new Date(dateStr);
  return date.toISOString().replace('T', ' // ').replace('Z', ' UTC');
};

// Generate a fake barcode pattern for visual effect
const generateBarcode = (text: string): string[] => {
  const pattern: string[] = [];
  for (let i = 0; i < 40; i++) {
    pattern.push(Math.random() > 0.5 ? 'thin' : 'thick');
  }
  return pattern;
};

// Generate a partner code from ID
const generatePartnerCode = (id: string | undefined): string => {
  if (!id) return 'PTR-000000';
  return `PTR-${id.slice(0, 6).toUpperCase()}`;
};

// Status configuration with cyberpunk colors
const getStatusConfig = (t: (key: string) => string): Record<PartnerStatus, {
  label: string;
  color: string;
  glow: string;
  icon: typeof CheckCircle;
  bgGradient: string;
}> => ({
  ACTIVE: {
    label: t('partnerModal.status.active'),
    color: '#00ff88',
    glow: '0 0 20px rgba(0,255,136,0.5)',
    icon: CheckCircle,
    bgGradient: 'linear-gradient(135deg, rgba(0,255,136,0.1) 0%, transparent 50%)'
  },
  PENDING: {
    label: t('partnerModal.status.pending'),
    color: '#ffaa00',
    glow: '0 0 20px rgba(255,170,0,0.5)',
    icon: Clock,
    bgGradient: 'linear-gradient(135deg, rgba(255,170,0,0.1) 0%, transparent 50%)'
  },
  SUSPENDED: {
    label: t('partnerModal.status.suspended'),
    color: '#ff8800',
    glow: '0 0 20px rgba(255,136,0,0.5)',
    icon: Clock,
    bgGradient: 'linear-gradient(135deg, rgba(255,136,0,0.1) 0%, transparent 50%)'
  },
  TERMINATED: {
    label: t('partnerModal.status.terminated'),
    color: '#ff4444',
    glow: '0 0 20px rgba(255,68,68,0.5)',
    icon: XCircle,
    bgGradient: 'linear-gradient(135deg, rgba(255,68,68,0.1) 0%, transparent 50%)'
  },
});

// Partner type configuration
const getTypeConfig = (t: (key: string) => string): Record<PartnerType, {
  label: string;
  color: string;
}> => ({
  REFERRAL: { label: t('partnerModal.type.referral'), color: '#00aaff' },
  RESELLER: { label: t('partnerModal.type.reseller'), color: '#aa66ff' },
  AFFILIATE: { label: t('partnerModal.type.affiliate'), color: '#ff8800' },
});

export const PartnerDetailModal: React.FC<PartnerDetailModalProps> = ({
  partner,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('platform');
  const printRef = useRef<HTMLDivElement>(null);
  const [scanLinePosition, setScanLinePosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Animated scan line effect
  useEffect(() => {
    if (!isOpen || !isAnimating) return;
    const interval = setInterval(() => {
      setScanLinePosition(prev => (prev + 1) % 100);
    }, 30);
    return () => clearInterval(interval);
  }, [isOpen, isAnimating]);

  // Stop animation after initial reveal
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const timeout = setTimeout(() => setIsAnimating(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Partner Profile - ${generatePartnerCode(partner?.id)}</title>
          <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
          <style>
            ${printStyles}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!isOpen || !partner) return null;

  const statusConfig = getStatusConfig(t);
  const typeConfig = getTypeConfig(t);
  const status = statusConfig[partner.status] || statusConfig.PENDING;
  const type = typeConfig[partner.type] || typeConfig.REFERRAL;
  const StatusIcon = status.icon;
  const barcode = generateBarcode(partner.id);
  const partnerCode = generatePartnerCode(partner.id);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-2xl max-h-[90vh] overflow-auto"
          style={{
            animation: 'modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Action buttons - outside printable area */}
          <div className="flex justify-end gap-2 mb-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)',
                border: '1px solid rgba(0,255,136,0.3)',
                color: '#00ff88',
                boxShadow: '0 0 15px rgba(0,255,136,0.2)',
              }}
            >
              <Printer className="w-4 h-4" />
              {t('partnerModal.print')}
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #2a1a1a 0%, #1a0f0f 100%)',
                border: '1px solid rgba(255,68,102,0.3)',
                color: '#ff4466',
              }}
            >
              <X className="w-4 h-4" />
              {t('partnerModal.close')}
            </button>
          </div>

          {/* Printable Content */}
          <div ref={printRef} className="receipt-container">
            <style>{modalStyles}</style>

            <div className="cyber-receipt">
              {/* Scan line effect */}
              {isAnimating && (
                <div
                  className="scan-line"
                  style={{ top: `${scanLinePosition}%` }}
                />
              )}

              {/* Circuit pattern overlay */}
              <div className="circuit-pattern" />

              {/* Header */}
              <div className="receipt-header">
                <div className="header-left">
                  <div className="logo-container">
                    <div className="logo-hex">
                      <Zap className="logo-icon" />
                    </div>
                    <div className="logo-text">
                      <span className="brand-name">COREX</span>
                      <span className="brand-sub">{t('partnerModal.partnerNetwork')}</span>
                    </div>
                  </div>
                </div>
                <div className="header-right">
                  <div className="doc-type">{t('partnerModal.partnerProfile')}</div>
                  <div className="doc-id">{partnerCode}</div>
                </div>
              </div>

              {/* Status Banner */}
              <div
                className="status-banner"
                style={{
                  borderColor: status.color,
                  boxShadow: status.glow,
                  background: status.bgGradient,
                }}
              >
                <div className="status-icon-container" style={{ color: status.color }}>
                  <StatusIcon className="status-icon" />
                  <div className="status-ring" style={{ borderColor: status.color }} />
                </div>
                <div className="status-text">
                  <span className="status-label" style={{ color: status.color }}>
                    {status.label}
                  </span>
                  <span className="status-time">
                    {t('partnerModal.joined')}: {formatFuturisticDate(partner.joinedAt)}
                  </span>
                </div>
              </div>

              {/* Partner Name Display */}
              <div className="partner-name-section">
                <div className="partner-avatar">
                  <Briefcase className="avatar-icon" />
                </div>
                <div className="partner-info">
                  <div className="partner-name">{partner.name}</div>
                  <div className="partner-company">{partner.company}</div>
                  <div className="partner-type" style={{ color: type.color }}>
                    {type.label}
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="content-grid">
                {/* Left Column - Contact Information */}
                <div className="detail-section">
                  <div className="section-header">
                    <Mail className="section-icon" />
                    <span>{t('partnerModal.sections.contactInfo')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.email')}</span>
                      <span className="detail-value highlight">{partner.email}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.partnerId')}</span>
                      <span className="detail-value mono">{partner.id?.slice(0, 12)}...</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.company')}</span>
                      <span className="detail-value">{partner.company || '---'}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column - Performance Metrics */}
                <div className="detail-section">
                  <div className="section-header">
                    <TrendingUp className="section-icon" />
                    <span>{t('partnerModal.sections.performance')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.commissionRate')}</span>
                      <span className="detail-value highlight">{partner.commissionRate}%</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.activeReferrals')}</span>
                      <span className="detail-value">{partner.activeReferrals}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('partnerModal.labels.partnerType')}</span>
                      <span className="detail-value">{t(`partners.type.${partner.type.toLowerCase()}`)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Earnings Display */}
              <div className="amount-section">
                <div className="amount-label">{t('partnerModal.totalEarnings')}</div>
                <div className="amount-value">
                  <span className="currency-symbol">$</span>
                  <span className="amount-number">
                    {partner.totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="currency-code">USD</span>
                </div>
                <div className="amount-subtext">
                  {t('partnerModal.lifetimeEarnings')}
                </div>
              </div>

              {/* Stats Row */}
              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-icon">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{partner.activeReferrals}</div>
                    <div className="stat-label">{t('partnerModal.statsLabels.referrals')}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Percent className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{partner.commissionRate}%</div>
                    <div className="stat-label">{t('partnerModal.statsLabels.commission')}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{t(`partners.type.${partner.type.toLowerCase()}`)}</div>
                    <div className="stat-label">{t('partnerModal.statsLabels.type')}</div>
                  </div>
                </div>
              </div>

              {/* Barcode Section */}
              <div className="barcode-section">
                <div className="barcode-container">
                  {barcode.map((bar, i) => (
                    <div
                      key={i}
                      className={`barcode-bar ${bar}`}
                      style={{ animationDelay: `${i * 20}ms` }}
                    />
                  ))}
                </div>
                <div className="barcode-text">{partnerCode}</div>
              </div>

              {/* Footer */}
              <div className="receipt-footer">
                <div className="footer-grid">
                  <div className="footer-item">
                    <span className="footer-label">{t('partnerModal.footer.joined')}</span>
                    <span className="footer-value">{formatFuturisticDate(partner.joinedAt)}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('partnerModal.footer.status')}</span>
                    <span className="footer-value">{t(`partners.status.${partner.status.toLowerCase()}`)}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('partnerModal.footer.verification')}</span>
                    <span className="footer-value verification">
                      {partner.id?.slice(0, 12).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="footer-line" />
                <div className="footer-disclaimer">
                  {t('partnerModal.footer.disclaimer')}
                  <br />
                  {t('partnerModal.footer.reference', { code: partnerCode })}
                </div>
                <div className="footer-brand">
                  <span className="powered-by">{t('partnerModal.footer.poweredBy')}</span>
                  <span className="brand">COREX-SUITE</span>
                  <span className="year">&copy; {new Date().getFullYear()}</span>
                </div>
              </div>

              {/* Corner decorations */}
              <div className="corner corner-tl" />
              <div className="corner corner-tr" />
              <div className="corner corner-bl" />
              <div className="corner corner-br" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Modal styles (displayed in browser)
const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes scanLine {
    0% { opacity: 0.8; }
    50% { opacity: 0.4; }
    100% { opacity: 0.8; }
  }

  @keyframes glowPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  @keyframes barReveal {
    from { transform: scaleY(0); }
    to { transform: scaleY(1); }
  }

  .cyber-receipt {
    position: relative;
    background: linear-gradient(180deg, #0a0a12 0%, #0f0f1a 50%, #0a0a12 100%);
    border: 1px solid rgba(0, 255, 136, 0.2);
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #e0e0e8;
  }

  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0,255,136,0.6), transparent);
    box-shadow: 0 0 10px rgba(0,255,136,0.8), 0 0 20px rgba(0,255,136,0.4);
    z-index: 10;
    pointer-events: none;
    animation: scanLine 0.5s ease-in-out infinite;
  }

  .circuit-pattern {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(0,255,136,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,255,136,0.03) 1px, transparent 1px);
    background-size: 20px 20px;
    pointer-events: none;
  }

  /* Header */
  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 1px solid rgba(0,255,136,0.1);
    background: linear-gradient(180deg, rgba(0,255,136,0.05) 0%, transparent 100%);
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #00ff88 0%, #00aa55 100%);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 20px rgba(0,255,136,0.4);
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #0a0a12;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #00ff88;
    text-shadow: 0 0 10px rgba(0,255,136,0.5);
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(0,255,136,0.5);
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #00ff88;
    letter-spacing: 1px;
  }

  /* Status Banner */
  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 1px solid;
    border-radius: 4px;
    position: relative;
  }

  .status-icon-container {
    position: relative;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-icon {
    width: 28px;
    height: 28px;
    z-index: 1;
  }

  .status-ring {
    position: absolute;
    inset: 0;
    border: 2px solid;
    border-radius: 50%;
    animation: glowPulse 2s ease-in-out infinite;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: rgba(255,255,255,0.4);
  }

  /* Partner Name Section */
  .partner-name-section {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 0 28px 20px;
  }

  .partner-avatar {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, rgba(0,255,136,0.2) 0%, rgba(0,170,85,0.1) 100%);
    border: 2px solid rgba(0,255,136,0.3);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #00ff88;
  }

  .avatar-icon {
    width: 32px;
    height: 32px;
  }

  .partner-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .partner-name {
    font-family: 'Orbitron', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 1px;
  }

  .partner-company {
    font-size: 14px;
    color: rgba(255,255,255,0.6);
  }

  .partner-type {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 2px;
    margin-top: 4px;
  }

  /* Content Grid */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(0,255,136,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(0,255,136,0.05);
    border-bottom: 1px solid rgba(0,255,136,0.1);
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(0,255,136,0.7);
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.35);
  }

  .detail-value {
    font-size: 13px;
    color: #e0e0e8;
  }

  .detail-value.highlight {
    color: #ffffff;
    font-weight: 500;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: rgba(0,255,136,0.8);
  }

  /* Amount Section */
  .amount-section {
    margin: 24px 28px;
    padding: 24px;
    background: linear-gradient(135deg, rgba(0,255,136,0.08) 0%, rgba(0,170,85,0.05) 100%);
    border: 1px solid rgba(0,255,136,0.2);
    border-radius: 4px;
    text-align: center;
    position: relative;
  }

  .amount-section::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 20%;
    right: 20%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00ff88, transparent);
  }

  .amount-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: rgba(0,255,136,0.6);
    margin-bottom: 12px;
  }

  .amount-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
  }

  .currency-symbol {
    font-family: 'Orbitron', sans-serif;
    font-size: 28px;
    font-weight: 600;
    color: rgba(0,255,136,0.6);
  }

  .amount-number {
    font-family: 'Orbitron', sans-serif;
    font-size: 48px;
    font-weight: 800;
    color: #00ff88;
    text-shadow: 0 0 30px rgba(0,255,136,0.5);
    letter-spacing: 2px;
  }

  .currency-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    color: rgba(255,255,255,0.3);
    align-self: flex-end;
    margin-bottom: 8px;
  }

  .amount-subtext {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 2px;
    color: rgba(255,255,255,0.25);
    margin-top: 12px;
  }

  /* Stats Row */
  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 0 28px 20px;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(0,255,136,0.1);
    border-radius: 4px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    background: rgba(0,255,136,0.1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #00ff88;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
  }

  .stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.4);
  }

  /* Barcode */
  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: linear-gradient(180deg, #00ff88 0%, rgba(0,255,136,0.6) 100%);
    border-radius: 1px;
    animation: barReveal 0.5s ease-out forwards;
    transform-origin: bottom;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: rgba(0,255,136,0.6);
  }

  /* Footer */
  .receipt-footer {
    padding: 20px 28px 24px;
    background: rgba(0,0,0,0.2);
    border-top: 1px solid rgba(0,255,136,0.1);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.3);
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: rgba(255,255,255,0.6);
  }

  .footer-value.verification {
    color: #00ff88;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0,255,136,0.2), transparent);
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: rgba(255,255,255,0.3);
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: rgba(0,255,136,0.6);
  }

  .year {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
  }

  /* Corner decorations */
  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: rgba(0,255,136,0.4);
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

// Print-specific styles
const printStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    background: #ffffff;
    padding: 20px;
  }

  .cyber-receipt {
    position: relative;
    background: #ffffff;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #1a1a2e;
    max-width: 600px;
    margin: 0 auto;
  }

  .scan-line,
  .circuit-pattern {
    display: none;
  }

  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 2px solid #0a0a12;
    background: #f8f8fa;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: #0a0a12;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #ffffff;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #0a0a12;
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: #666;
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: #666;
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #0a0a12;
    letter-spacing: 1px;
  }

  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    background: #f8f8fa;
  }

  .status-icon-container {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0a0a12;
  }

  .status-icon {
    width: 28px;
    height: 28px;
  }

  .status-ring {
    display: none;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
    color: #0a0a12;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #666;
  }

  .partner-name-section {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 0 28px 20px;
  }

  .partner-avatar {
    width: 64px;
    height: 64px;
    background: #f0f0f0;
    border: 2px solid #0a0a12;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0a0a12;
  }

  .avatar-icon {
    width: 32px;
    height: 32px;
  }

  .partner-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .partner-name {
    font-family: 'Orbitron', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #0a0a12;
    letter-spacing: 1px;
  }

  .partner-company {
    font-size: 14px;
    color: #666;
  }

  .partner-type {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 2px;
    margin-top: 4px;
    color: #333;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #eee;
    border-bottom: 1px solid #ddd;
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: #333;
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: #888;
  }

  .detail-value {
    font-size: 13px;
    color: #1a1a2e;
  }

  .detail-value.highlight {
    color: #000;
    font-weight: 600;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #333;
  }

  .amount-section {
    margin: 24px 28px;
    padding: 24px;
    background: #f8f8fa;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    text-align: center;
  }

  .amount-section::before {
    display: none;
  }

  .amount-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: #666;
    margin-bottom: 12px;
  }

  .amount-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
  }

  .currency-symbol {
    font-family: 'Orbitron', sans-serif;
    font-size: 28px;
    font-weight: 600;
    color: #333;
  }

  .amount-number {
    font-family: 'Orbitron', sans-serif;
    font-size: 48px;
    font-weight: 800;
    color: #0a0a12;
    letter-spacing: 2px;
  }

  .currency-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 14px;
    color: #666;
    align-self: flex-end;
    margin-bottom: 8px;
  }

  .amount-subtext {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 2px;
    color: #888;
    margin-top: 12px;
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 0 28px 20px;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    background: #eee;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0a0a12;
  }

  .stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: #888;
  }

  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: #0a0a12;
    border-radius: 1px;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: #333;
  }

  .receipt-footer {
    padding: 20px 28px 24px;
    background: #f8f8fa;
    border-top: 2px solid #0a0a12;
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: #888;
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #333;
  }

  .footer-value.verification {
    color: #0a0a12;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: #ddd;
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: #666;
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: #888;
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: #0a0a12;
  }

  .year {
    font-size: 8px;
    color: #888;
  }

  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: #0a0a12;
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

export default PartnerDetailModal;
